<template>
  <ExpandableCell
    :data-key="dataKey"
    :field="column.field"
    @toggleExpansion="$emit('toggleExpansion', $event)"
  >
    <!-- Collapsed content: compact dd-mm-yy format -->
    <template #collapsed>
      {{ compactDate }}
    </template>
    <!-- Expanded content: full formatted date -->

    <template #expanded>
      {{ fullDate }}
    </template>
  </ExpandableCell>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatDate } from '@/utils/dateUtils';

import type { PaginatedDataTableCellProps } from './templateTypes';

defineEmits(['toggleExpansion']);

const props = defineProps<PaginatedDataTableCellProps<string | Date>>();
const { locale } = useI18n();

// Full formatted date (expanded)
const fullDate = computed(() => {
  const rawValue = props.cell as string | Date | undefined;
  if (!rawValue) return '';
  return formatDate(rawValue, locale.value, 'datetime');
});

// Compact date format: dd-mm-yy (collapsed)
const compactDate = computed(() => {
  const rawValue = props.cell as string | Date | undefined;
  if (!rawValue) return '';

  const date = typeof rawValue === 'string' ? new Date(rawValue) : rawValue;
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2); // Last 2 digits
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year}, ${hours}:${minutes}`;
});
</script>
